import { useState, useEffect, useCallback, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import { format } from "date-fns";
import { Plus, LogIn, LogOut, Loader2, CalendarClock, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import AddBookingModal from "@/components/AddBookingModal";
import { NEUTRAL_BORDER, softTint, PILL, buildTherapistColorMap } from "@/lib/therapistColors";
import {
  getEffectiveRoster, getTransactions, getAttendance, getStaff, getServices,
  clockIn, clockOut, setAttendance as saveAttendance,
} from "@/lib/api";

const DAY_START = 9 * 60;   // 09:00
const DAY_END = 22 * 60;    // 22:00
const SLOT_MIN = 30;
const ROW_H = 30;           // px per 30-min slot
const HEADER_H = 108;        // px therapist header height
const TIME_COL = 56;

const SLOTS = Array.from({ length: (DAY_END - DAY_START) / SLOT_MIN }, (_, i) => DAY_START + i * SLOT_MIN);
const toHHMM = (mins) => `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
const toMinutes = (t) => {
  const [h, m] = (t || "0:0").split(":").map(Number);
  return h * 60 + m;
};
const fmtClock = (iso) => {
  try { return format(new Date(iso), "h:mm a"); } catch { return ""; }
};

export default function ScheduleBoard() {
  const { selectedBranch, branches, selectedDate } = useOutletContext();
  const branchId = selectedBranch || branches[0]?.id;
  const dateStr = format(selectedDate, "yyyy-MM-dd");

  const [allStaff, setAllStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [roster, setRoster] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [editTx, setEditTx] = useState(null);
  const [clockingId, setClockingId] = useState(null);
  const [editAtt, setEditAtt] = useState(null); // { staff_id, staff_name, in, out }
  const [savingTimes, setSavingTimes] = useState(false);

  const fetchData = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const [staffData, svcData, rosterData, txData, attData] = await Promise.all([
        getStaff(),
        getServices(),
        getEffectiveRoster(dateStr, branchId),
        getTransactions({ date: dateStr, branch_id: branchId }),
        getAttendance(dateStr, branchId),
      ]);
      setAllStaff(staffData);
      setServices(svcData);
      setRoster(rosterData);
      setTransactions(txData);
      setAttendance(Array.isArray(attData) ? attData : []);
    } catch {
      toast.error("Failed to load schedule");
    } finally {
      setLoading(false);
    }
  }, [branchId, dateStr]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Columns ordered by arrival (clock-in) sequence; not-yet-checked-in sort last alphabetically.
  const columns = useMemo(() => {
    const map = new Map();
    roster.forEach((r) => map.set(r.staff_id, r.staff_name));
    transactions.forEach((t) => {
      (t.therapist_ids || [t.staff_id]).forEach((id, i) => {
        if (!map.has(id)) map.set(id, (t.therapist_names && t.therapist_names[i]) || t.staff_name);
      });
    });
    attendance.forEach((a) => { if (!map.has(a.staff_id)) map.set(a.staff_id, a.staff_name); });
    const arr = Array.from(map.entries()).map(([id, name]) => {
      const att = attendance.find((a) => a.staff_id === id);
      return { id, name, clock_in: att?.clock_in || null };
    });
    arr.sort((a, b) => {
      if (a.clock_in && b.clock_in) return a.clock_in.localeCompare(b.clock_in);
      if (a.clock_in) return -1;
      if (b.clock_in) return 1;
      return a.name.localeCompare(b.name);
    });
    let q = 0;
    return arr.map((c) => ({ ...c, queue: c.clock_in ? ++q : null }));
  }, [roster, transactions, attendance]);

  const attFor = useCallback((sid) => attendance.find((a) => a.staff_id === sid), [attendance]);

  // Same color mapping as DataTable: built from the day's session therapists.
  const therapistColorMap = useMemo(() => {
    const names = [];
    transactions.forEach((t) => {
      (t.therapist_names && t.therapist_names.length ? t.therapist_names : [t.staff_name]).forEach((n) => names.push(n));
    });
    return buildTherapistColorMap(names);
  }, [transactions]);
  const colorFor = (name) => therapistColorMap[name] || NEUTRAL_BORDER;

  const sessionsFor = useCallback(
    (sid) => transactions.filter((t) => (t.therapist_ids || [t.staff_id]).includes(sid)),
    [transactions]
  );

  const handleClock = async (sid) => {
    const att = attFor(sid);
    setClockingId(sid);
    try {
      if (att && att.clock_in && !att.clock_out) {
        await clockOut({ staff_id: sid, date: dateStr, branch_id: branchId });
        toast.success("Clocked out");
      } else {
        await clockIn({ staff_id: sid, date: dateStr, branch_id: branchId });
        toast.success("Clocked in");
      }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clock action failed");
    } finally {
      setClockingId(null);
    }
  };

  const openCell = (sid, mins) => {
    setPrefill({ staff_id: sid, time_start: toHHMM(mins) });
    setModalOpen(true);
  };

  const isoFromTime = (t) => {
    if (!t) return null;
    const [h, m] = t.split(":").map(Number);
    const d = selectedDate;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, m, 0).toISOString();
  };
  const timeFromIso = (iso) => {
    if (!iso) return "";
    try {
      const dt = new Date(iso);
      return `${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`;
    } catch { return ""; }
  };

  const openEditTimes = (s) => {
    const att = attFor(s.id);
    setEditAtt({
      staff_id: s.id, staff_name: s.name,
      in: timeFromIso(att?.clock_in), out: timeFromIso(att?.clock_out),
    });
  };

  const handleSaveTimes = async () => {
    if (!editAtt) return;
    if (editAtt.in && editAtt.out && editAtt.out <= editAtt.in) {
      toast.error("Clock-out must be after clock-in");
      return;
    }
    setSavingTimes(true);
    try {
      await saveAttendance({
        staff_id: editAtt.staff_id, date: dateStr, branch_id: branchId,
        clock_in: isoFromTime(editAtt.in), clock_out: isoFromTime(editAtt.out),
      });
      toast.success("Attendance updated — earnings recalculated");
      setEditAtt(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update attendance");
    } finally {
      setSavingTimes(false);
    }
  };

  const gridHeight = SLOTS.length * ROW_H;

  return (
    <div className="animate-in" data-testid="schedule-board">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5 text-forest" />
          <h2 className="font-heading text-xl font-bold text-forest">Schedule</h2>
          <span className="text-sm text-sage">· {format(selectedDate, "EEE, d MMM yyyy")} · {columns.length} therapists</span>
        </div>
        <Button
          className="bg-forest hover:bg-forest-light"
          onClick={() => { setPrefill(null); setModalOpen(true); }}
          data-testid="new-booking-btn"
        >
          <Plus className="h-4 w-4 mr-2" /> New Booking
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-80 bg-white border border-border rounded-xl">
          <Loader2 className="h-8 w-8 animate-spin text-forest" />
        </div>
      ) : columns.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-80 bg-white border border-border rounded-xl text-sage">
          <p>No therapists rostered for this day.</p>
          <p className="text-sm">Assign staff in Admin → Roster, or use New Booking to log a session.</p>
        </div>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <div className="flex">
            {/* Time column */}
            <div className="shrink-0 border-r border-border bg-sand/40" style={{ width: TIME_COL }}>
              <div style={{ height: HEADER_H }} className="border-b border-border" />
              {SLOTS.map((m) => (
                <div key={m} className="text-[10px] font-semibold text-sage px-1 border-b border-border/40 flex items-start justify-end pr-1.5" style={{ height: ROW_H }}>
                  {m % 60 === 0 ? toHHMM(m) : ""}
                </div>
              ))}
            </div>

            {/* Therapist columns */}
            <div className="flex-1 flex overflow-x-auto">
              {columns.map((s) => {
                const att = attFor(s.id);
                const active = att && att.clock_in && !att.clock_out;
                const ended = att && att.clock_out;
                const solid = colorFor(s.name);
                const tint = softTint(solid, 0.6);
                let statusText = "Not Clocked In";
                if (active) statusText = `Started at ${fmtClock(att.clock_in)}`;
                else if (ended) statusText = `Ended at ${fmtClock(att.clock_out)}`;
                return (
                  <div key={s.id} className="flex-1 min-w-[150px] border-r border-border last:border-r-0" data-testid={`schedule-col-${s.id}`}>
                    {/* Header */}
                    <div className="relative border-b border-border px-2 py-2 flex flex-col items-center gap-1.5 bg-white" style={{ height: HEADER_H }}>
                      {s.queue && (
                        <span className="absolute top-1 left-1 w-5 h-5 rounded-full bg-ochre text-white text-[10px] font-bold flex items-center justify-center" data-testid={`queue-badge-${s.id}`}>
                          {s.queue}
                        </span>
                      )}
                      <span
                        style={{ ...PILL, padding: "3px 10px", gap: "6px", color: "#1E293B", fontWeight: 600, maxWidth: "100%" }}
                        className="text-xs truncate"
                      >
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: solid }} />
                        <span className="truncate">{s.name}</span>
                      </span>
                      <span className={`text-[11px] ${active ? "text-forest font-medium" : ended ? "text-sage" : "text-sage/70"}`} data-testid={`clock-status-${s.id}`}>
                        {statusText}
                      </span>
                      <div className="flex items-center gap-1">
                        {!ended && (
                          <Button
                            size="sm"
                            variant={active ? "outline" : "default"}
                            className={`h-7 text-xs ${active ? "border-terracotta text-terracotta hover:bg-terracotta/10" : "bg-forest hover:bg-forest-light"}`}
                            onClick={() => handleClock(s.id)}
                            disabled={clockingId === s.id}
                            data-testid={`clock-btn-${s.id}`}
                          >
                            {clockingId === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : active ? <><LogOut className="h-3 w-3 mr-1" />Clock Out</> : <><LogIn className="h-3 w-3 mr-1" />Clock In</>}
                          </Button>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-sage hover:text-forest"
                          onClick={() => openEditTimes(s)}
                          title="Edit / backdate clock times"
                          data-testid={`edit-times-btn-${s.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    {/* Slot grid */}
                    <div className="relative" style={{ height: gridHeight }}>
                      {SLOTS.map((m, idx) => (
                        <div
                          key={m}
                          className={`border-b border-border/40 hover:bg-forest/5 cursor-pointer ${idx % 2 ? "bg-sand/20" : ""}`}
                          style={{ height: ROW_H }}
                          onClick={() => openCell(s.id, m)}
                          data-testid={`slot-${s.id}-${toHHMM(m)}`}
                        />
                      ))}
                      {sessionsFor(s.id).map((t) => {
                        const top = ((toMinutes(t.time_start) - DAY_START) / SLOT_MIN) * ROW_H;
                        const height = ((toMinutes(t.time_end) - toMinutes(t.time_start)) / SLOT_MIN) * ROW_H;
                        const isDual = (t.therapist_ids || []).length > 1;
                        return (
                          <div
                            key={t.id}
                            className="absolute left-1 right-1 rounded-md px-1.5 py-1 overflow-hidden shadow-sm cursor-pointer hover:ring-2 hover:ring-forest/40"
                            style={{ top: `${Math.max(0, top)}px`, height: `${Math.max(24, height - 2)}px`, backgroundColor: tint, borderLeft: `4px solid ${solid}` }}
                            onClick={(e) => { e.stopPropagation(); setEditTx(t); }}
                            data-testid={`session-block-${t.id}`}
                          >
                            <div className="flex items-center justify-between gap-1">
                              <span className="text-[11px] truncate" style={{ fontWeight: 700, color: "#1E293B" }}>{t.client_name}</span>
                              {t.booking_status === "Book" && (
                                <span style={{ ...PILL, padding: "1px 8px", color: "#D97706", fontWeight: 600 }} className="text-[8px] shrink-0">
                                  Booked
                                </span>
                              )}
                            </div>
                            <p className="text-[10px] truncate mt-0.5" style={{ color: "#1E293B", opacity: 0.9 }}>
                              {t.service_name}{isDual ? " · 4-Hand" : ""}
                            </p>
                            <p className="text-[9px] mt-0.5" style={{ fontWeight: 500, color: "#475569" }}>
                              {t.time_start}–{t.time_end}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <AddBookingModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        branchId={branchId}
        date={selectedDate}
        staff={allStaff}
        services={services}
        prefill={prefill}
        onCreated={() => { setModalOpen(false); fetchData(); }}
      />

      <AddBookingModal
        open={!!editTx}
        onOpenChange={(v) => { if (!v) setEditTx(null); }}
        branchId={branchId}
        date={selectedDate}
        staff={allStaff}
        services={services}
        editTransaction={editTx}
        onCreated={() => { setEditTx(null); fetchData(); }}
      />

      <Dialog open={!!editAtt} onOpenChange={(o) => { if (!o) setEditAtt(null); }}>
        <DialogContent className="max-w-sm" data-testid="edit-times-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-forest">Adjust Clock Times</DialogTitle>
            <DialogDescription>
              {editAtt?.staff_name} · {format(selectedDate, "EEE, d MMM yyyy")} — manually set or backdate. Earnings recalculate automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-2">
              <Label className="text-sm text-forest">Clock In</Label>
              <Input type="time" value={editAtt?.in || ""} onChange={(e) => setEditAtt((p) => ({ ...p, in: e.target.value }))} data-testid="edit-clock-in" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-forest">Clock Out</Label>
              <Input type="time" value={editAtt?.out || ""} onChange={(e) => setEditAtt((p) => ({ ...p, out: e.target.value }))} data-testid="edit-clock-out" />
            </div>
          </div>
          <p className="text-xs text-sage/80">Leave a field blank to clear it. Shift hours = Clock Out − Clock In.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditAtt(null)}>Cancel</Button>
            <Button onClick={handleSaveTimes} disabled={savingTimes} className="bg-forest hover:bg-forest-light" data-testid="save-times-btn">
              {savingTimes ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Times"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
